import React, { Fragment } from 'react'
import { Spin, Tag, Table, Alert, Button } from 'antd'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EditOutlined,
  FrownOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons'

export default function PriceComparatorTable({
  columnsFields = ['url', 'date', 'price'],
  data,
  country = 'es',
  rest,
  selectedInTables = [],
  setSelectedRowKeys,
  displayLinks = true,
  onUpdatePage,
  onAdd,
  page = 0,
}) {
  const dataToRender = groupByRef(data, country)
  const priceFormatter = new Intl.NumberFormat('es', {
    style: 'currency',
    currency: country === 'uk' ? 'GBP' : 'EUR',
  })
  const dataToFilter = {
    url: [...new Set(data?.map?.((item) => item.url))],
  }

  function mapColumn(key) {
    let column = {
      title: key,
      dataIndex: key,
      key,
      sorter: (a, b) => {
        if (a[key] < b[key]) return -1
        if (a[key] > b[key]) return 1
        return 0
      },
    }

    if (key === 'url') {
      column.render = (text, record) => {
        if (!text) {
          return (
            <>
              <FrownOutlined />{' '}
              <i>
                No results found.{' '}
                {onAdd ? (
                  <>
                    You can{' '}
                    <a href="#" onClick={() => onAdd(record.product_group)}>
                      add
                    </a>{' '}
                    competitor URLs.
                  </>
                ) : (
                  ''
                )}
              </i>
            </>
          )
        }
        const url = new URL(text)
        return (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {url.hostname}
          </a>
        )
      }
    }

    if (key === 'price') {
      column.render = (text, record) => {
        if (!text) return ''
        return new Intl.NumberFormat('es', {
          style: 'currency',
          currency: record.currency,
        }).format(text)
      }
    }

    if (key === 'date') {
      column.render = (text, record) => {
        if (!text) return ''
        const date = new Date(text)
        const isNew = date.getTime() > Date.now() - 5000

        return isNew ? (
          <Tag color="green">Now</Tag>
        ) : (
          new Intl.DateTimeFormat('es').format(date)
        )
      }
    }

    if (dataToFilter[key]) {
      column = {
        ...column,
        filters: dataToFilter[key].map((value) => ({ text: value, value })),
        filterMode: 'tree',
        filterSearch: true,
        onFilter: (value, record) => record[key].includes(value),
      }
    }

    return column
  }

  const restColumns = [
    'anyada',
    'capacity',
    'product_group',
    'url',
    'price',
  ].map(mapColumn)
  const columns = columnsFields.map(mapColumn)

  if (!data) {
    return (
      <div style={{ margin: 'auto' }}>
        <Spin />
      </div>
    )
  }

  return (
    <>
      {dataToRender.length === 0 && (
        <>
          <Alert
            message="No data"
            description={
              <>
                There is no data to show.{' '}
                {onAdd ? (
                  <>
                    You can{' '}
                    <a href="#" onClick={() => onAdd()}>
                      add
                    </a>{' '}
                    competitor URLs.
                  </>
                ) : (
                  ''
                )}
              </>
            }
            type="info"
            showIcon
          />
          {onUpdatePage && page > 0 && (
            <div
              style={{
                display: 'flex',
                marginTop: 20,
                justifyContent: 'flex-end',
              }}
            >
              <Button onClick={() => onUpdatePage(page - 1)}>
                <ArrowLeftOutlined /> Prev page
              </Button>
            </div>
          )}
        </>
      )}
      {dataToRender.map((p) => {
        const productUrl = `https://www.vinissimus.com/es/vino/${p.product_group}`
        const editUrl = p.path
          ? window.location.origin +
            `/-admin/?path=${p.path}/&tab=Price+comparator`
          : undefined

        const linksEl = (
          <>
            {editUrl && (
              <a
                href={editUrl}
                target="_blank"
                title={editUrl}
                style={{ marginLeft: 10 }}
                rel="noopener noreferrer"
              >
                <EditOutlined />
              </a>
            )}
            <a
              href={productUrl}
              target="_blank"
              title={productUrl}
              style={{ marginLeft: 10 }}
              rel="noopener noreferrer"
            >
              <LinkOutlined />
            </a>
            {onAdd && (
              <a
                href="#"
                onClick={() => onAdd(p.product_group)}
                title="Add new URL"
                style={{ marginLeft: 10 }}
              >
                <PlusOutlined />
              </a>
            )}
          </>
        )

        const titleEl = (
          <>
            <b>{p.our_name}</b> -{' ('}
            {p.id_producto}
            {')'}
            {displayLinks && linksEl}
          </>
        )
        const offerPriceEl = p.our_offer_price ? (
          <b
            style={{
              marginLeft: 5,
              color: '#e03100',
              fontWeight: 'bold',
              padding: 5,
              borderRadius: 2,
            }}
          >
            {priceFormatter.format(
              p.our_offer_price + p.our_offer_price * p.our_taxes
            )}
          </b>
        ) : null
        const priceEl = p.our_price ? (
          <>
            <b
              style={{
                textDecoration: p.our_offer_price ? 'line-through' : undefined,
              }}
            >
              {priceFormatter.format(p.our_price + p.our_price * p.our_taxes)}
            </b>
            {offerPriceEl}
          </>
        ) : null

        return (
          <Fragment key={p.id_producto}>
            <Alert
              style={{ marginTop: 15 }}
              message={titleEl}
              action={priceEl}
              type="info"
            />
            <Table
              dataSource={p.competitors}
              key={p.id_producto}
              columns={columns}
              pagination={false}
              rowKey={(record) =>
                `${record.url}-///-${record.anyada}-${record.capacity}`
              }
              size="small"
              rowSelection={
                setSelectedRowKeys
                  ? {
                      selectedRowKeys: selectedInTables[p.id_producto] || [],
                      onChange: (v) =>
                        setSelectedRowKeys((r) => ({
                          ...r,
                          [p.id_producto]: v,
                        })),
                    }
                  : undefined
              }
            />
          </Fragment>
        )
      })}
      {rest?.length > 0 && (
        <details>
          <summary
            style={{
              marginTop: 30,
              padding: 10,
              backgroundColor: '#fffbe6',
              border: '1px solid #ffe58f',
            }}
          >
            <b>More {rest.length} URLs</b> - we do not have this option to
            compare with our price.
          </summary>
          <Table
            dataSource={rest}
            columns={restColumns}
            pagination={false}
            rowKey={(record) =>
              `${record.url}-///-${record.anyada}-${record.capacity}`
            }
            size="small"
          />
        </details>
      )}
      {onUpdatePage && (
        <div
          style={{
            display: 'flex',
            marginTop: 20,
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          {page > 0 && (
            <Button onClick={() => onUpdatePage(page - 1)}>
              <ArrowLeftOutlined /> Prev page
            </Button>
          )}
          <Button onClick={() => onUpdatePage(page + 1)}>
            Next page <ArrowRightOutlined />
          </Button>
        </div>
      )}
    </>
  )
}

function groupByRef(data = [], country) {
  const dataInCountry = data?.filter?.(
    (item) => item.delivery_country === country
  )

  if (!dataInCountry) return []

  const result = {}

  for (let item of dataInCountry) {
    const ref = item.id_producto || 'more'
    result[ref] = {
      competitors: result[ref] ? [...result[ref].competitors, item] : [item],
      id_producto: ref,
      our_name: item.our_name || 'More',
      our_offer_price: item.our_offer_price,
      our_price: item.our_price,
      our_taxes: item.our_taxes,
      path: item.path,
      product_group: item.product_group,
    }
  }

  return Object.values(result)
}
